import { Triangle } from "lucide-react";

import type {
  LayerStats,
  LayerValue,
  SoilTextureClass,
  SoilTextureScheme,
} from "../types/api";
import { SoilTextureTriangle } from "./SoilTextureTriangle";

type Props = {
  schemes: SoilTextureScheme[];
  classifications: SoilTextureClass[] | null | undefined;
  /** Soil-domain rows from /api/point or /api/zone, used to pull sand/clay. */
  soil: (LayerValue | LayerStats)[] | undefined;
  /**
   * How many triangles per row. 1 gives a stack of large triangles (good
   * inside the right-side drawer); 4 keeps the original wide-grid layout.
   */
  columns?: 1 | 2 | 4;
};

function isStats(r: LayerValue | LayerStats): r is LayerStats {
  return "mean" in r;
}

function pickSandClay(
  rows: (LayerValue | LayerStats)[] | undefined,
): { sand: number; clay: number } | null {
  if (!rows?.length) return null;
  const byCode: Record<string, LayerValue | LayerStats> = {};
  for (const r of rows) byCode[r.code] = r;
  const sand = byCode["sand"];
  const clay = byCode["clay"];
  if (!sand || !clay) return null;
  // Soil layers ingest as g/kg → divide by 10 for percent. Both point
  // (value) and zone (mean) responses follow the same convention.
  const sv = isStats(sand) ? sand.mean : sand.value;
  const cv = isStats(clay) ? clay.mean : clay.value;
  if (sv == null || cv == null) return null;
  return { sand: sv / 10, clay: cv / 10 };
}

export function SoilTextureCard({ schemes, classifications, soil, columns = 4 }: Props) {
  const sample = pickSandClay(soil);
  // Hide the whole block until we have both sand and clay — there's nothing
  // to mark on the triangle without them.
  if (!sample || !schemes.length) return null;

  const classBy: Record<string, SoilTextureClass | undefined> = {};
  for (const k of classifications ?? []) classBy[k.scheme] = k;

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <div className="mb-3 flex items-center gap-2">
        <Triangle className="h-4 w-4 text-mef-orange" />
        <h3 className="mef-caption">Soil Texture</h3>
        <span className="ml-auto font-mono text-[10px] tabular-nums text-black/40">
          sand {sample.sand.toFixed(1)}% · clay {sample.clay.toFixed(1)}% · silt{" "}
          {Math.max(0, 100 - sample.sand - sample.clay).toFixed(1)}%
        </span>
      </div>

      <div
        className={[
          "grid gap-4",
          columns === 1
            ? "grid-cols-1"
            : columns === 2
              ? "grid-cols-2"
              : "grid-cols-2 sm:grid-cols-4",
        ].join(" ")}
      >
        {schemes.map((sc) => {
          const k = classBy[sc.scheme];
          return (
            <div key={sc.scheme} className="flex flex-col">
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <div className="text-[11px] uppercase tracking-[0.04em] text-black/40">
                  {sc.scheme}
                </div>
                <div className="text-[12px] font-medium text-black/80 truncate">
                  {k?.class_name ?? "—"}
                </div>
              </div>
              <SoilTextureTriangle
                scheme={sc}
                sample={sample}
                highlightedClassId={k?.class_id ?? null}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
